"use strict";
import { AddPasskeyMenu } from "components/AccountDrawer/PasskeyMenu/AddPasskeyMenu";
import { DeletePasskeyMenu } from "components/AccountDrawer/PasskeyMenu/DeletePasskeyMenu";
import { DeletePasskeySpeedbumpMenu } from "components/AccountDrawer/PasskeyMenu/DeletePasskeySpeedbumpMenu";
import { PasskeyMenuModalState } from "components/AccountDrawer/PasskeyMenu/PasskeyMenuModal";
import { VerifyPasskeyMenu } from "components/AccountDrawer/PasskeyMenu/VerifyPasskeyMenu";
import { SlideOutMenu } from "components/AccountDrawer/SlideOutMenu";
import { MenuColumn } from "components/AccountDrawer/shared";
import { AndroidLogo } from "components/Icons/AndroidLogo";
import { AppleLogo } from "components/Icons/AppleLogo";
import { usePasskeyAuthWithHelpModal } from "hooks/usePasskeyAuthWithHelpModal";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { LifeBuoy } from "react-feather";
import { useTranslation } from "react-i18next";
import { ClickableTamaguiStyle } from "theme/components/styles";
import { Anchor, Button, Flex, Image, Loader, Text, TouchableArea, useSporeColors } from "ui/src";
import { CHROME_LOGO } from "ui/src/assets";
import { Passkey } from "ui/src/components/icons/Passkey";
import { Trash } from "ui/src/components/icons/Trash";
import { Windows } from "ui/src/components/icons/Windows";
import { iconSizes } from "ui/src/theme";
import { uniswapUrls } from "constants/urls";
import {
  Action,
  AuthenticatorNameType,
  authenticateWithPasskey,
  listAuthenticators
} from "features/passkey/embeddedWallet";
import { isMobileWeb } from "utilities/src/platform";
import { useEvent } from "utilities/src/react/hooks";
var AuthenticatorProvider = /* @__PURE__ */ ((AuthenticatorProvider2) => {
  AuthenticatorProvider2["Google"] = "Chrome";
  AuthenticatorProvider2["Apple"] = "iCloud";
  AuthenticatorProvider2["Microsoft"] = "Windows";
  AuthenticatorProvider2["Android"] = "Android";
  AuthenticatorProvider2["Other"] = "Other";
  return AuthenticatorProvider2;
})(AuthenticatorProvider || {});
function getProviderIcon(provider, colors) {
  switch (provider) {
    case "Chrome" /* Google */:
      return <Image height={iconSizes.icon20} source={CHROME_LOGO} width={iconSizes.icon20} />;
    case "iCloud" /* Apple */:
      return <AppleLogo height={iconSizes.icon20} width={iconSizes.icon20} fill={colors.neutral1.val} />;
    case "Android" /* Android */:
      return <AndroidLogo height={iconSizes.icon20} width={iconSizes.icon20} />;
    case "Windows" /* Microsoft */:
      return <Windows size="$icon.20" color="$neutral1" />;
    default:
      return <Passkey size="$icon.20" color="$neutral1" />;
  }
}
function getProviderLabel(provider, count) {
  switch (provider) {
    case "Android" /* Android */:
    case "Windows" /* Microsoft */:
    case "iCloud" /* Apple */:
    case "Chrome" /* Google */: {
      return provider;
    }
    default: {
      return t("common.passkey.count", { number: count });
    }
  }
}
function getProvider(providerName) {
  switch (providerName) {
    case AuthenticatorNameType.GOOGLE_PASSWORD_MANAGER:
      return "Android" /* Android */;
    case AuthenticatorNameType.CHROME_MAC:
      return "Chrome" /* Google */;
    case AuthenticatorNameType.ICLOUD_KEYCHAIN:
    case AuthenticatorNameType.ICLOUD_KEYCHAIN_MANAGED:
      return "iCloud" /* Apple */;
    case AuthenticatorNameType.WINDOWS_HELLO:
      return "Windows" /* Microsoft */;
    default:
      return "Other" /* Other */;
  }
}
function convertAuthenticatorsToDisplay(authenticators) {
  let otherPasskeyCount = 1;
  return authenticators.map((authenticator) => {
    const provider = getProvider(authenticator.providerName);
    const isOtherPasskey = provider === "Other" /* Other */;
    const label = getProviderLabel(provider, otherPasskeyCount);
    isOtherPasskey && otherPasskeyCount++;
    return {
      ...authenticator,
      provider,
      label
    };
  });
}
const AuthenticatorRow = ({
  authenticator,
  handleDeletePasskey
}) => {
  const { t: t2 } = useTranslation();
  const colors = useSporeColors();
  const [showDeleteIcon, setShowDeleteIcon] = useState(false);
  const createdAtDate = authenticator.creationTime?.toDate();
  const isValidDate = createdAtDate instanceof Date && !isNaN(createdAtDate.getTime());
  const formattedDate = createdAtDate?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: createdAtDate?.getFullYear() === (/* @__PURE__ */ new Date()).getFullYear() ? void 0 : "numeric"
  });
  return <Flex
    row
    gap="$gap12"
    alignItems="center"
    onHoverIn={() => setShowDeleteIcon(true)}
    onHoverOut={() => setShowDeleteIcon(false)}
    pb="$padding16"
  >
      <Flex
    height={40}
    width={40}
    background="$surface2"
    borderRadius="$rounded12"
    alignItems="center"
    justifyContent="center"
  >
        {getProviderIcon(authenticator.provider, colors)}
      </Flex>
      <Flex>
        <Text variant="body2">{authenticator.label}</Text>
        {isValidDate && <Text variant="body3" color="$neutral2">
            {t2("common.created.date", { date: formattedDate })}
          </Text>}
      </Flex>
      {(showDeleteIcon || isMobileWeb) && <TouchableArea
    ml="auto"
    onPress={() => {
      handleDeletePasskey(authenticator);
    }}
  >
          <Trash color="$statusCritical" size={24} />
        </TouchableArea>}
    </Flex>;
};
function LoadingPasskeyRow() {
  return <Flex row gap="$gap12" alignItems="center" pb="$padding16">
      <Loader.Box borderRadius="$roundedFull" height={40} width={40} opacity={0.5} />
      <Flex gap="$gap8">
        <Loader.Box borderRadius="$rounded12" height={14} width={72} opacity={0.5} />
        <Loader.Box borderRadius="$rounded12" height={12} width={112} opacity={0.5} />
      </Flex>
    </Flex>;
}
export default function PasskeyMenu({ onClose }) {
  const { t: t2 } = useTranslation();
  const colors = useSporeColors();
  const [authenticators, setAuthenticators] = useState([]);
  const [passkeyMenuModalState, setPasskeyMenuModalState] = useState(void 0);
  const [selectedAuthenticator, setSelectedAuthenticator] = useState(void 0);
  const [credential, setCredential] = useState(void 0);
  const [actionAfterVerify, setActionAfterVerify] = useState(void 0);
  const { mutate: refreshAuthenticators, isPending: areAuthenticatorsLoading } = usePasskeyAuthWithHelpModal(
    async () => {
      const authenticators2 = await listAuthenticators();
      const authenticatorsDisplay = convertAuthenticatorsToDisplay(authenticators2);
      authenticatorsDisplay.sort((a, b) => {
        const aDate = a.creationTime?.toDate();
        const bDate = b.creationTime?.toDate();
        if (!aDate || !bDate) {
          return 0;
        }
        return aDate.getTime() - bDate.getTime();
      });
      return authenticatorsDisplay;
    },
    {
      onSuccess: (authenticatorsDisplay) => {
        setAuthenticators(authenticatorsDisplay);
      }
    }
  );
  const { mutate: verifyPasskey } = usePasskeyAuthWithHelpModal(
    async () => {
      return await authenticateWithPasskey(
        actionAfterVerify === PasskeyMenuModalState.ADD_PASSKEY ? Action.REGISTER_NEW_AUTHENTICATION_TYPES : Action.DELETE_RECORD
      );
    },
    {
      onSuccess: (credential2) => {
        setCredential(credential2);
        setPasskeyMenuModalState(actionAfterVerify);
        setActionAfterVerify(void 0);
      }
    }
  );
  useEffect(() => {
    refreshAuthenticators();
  }, [refreshAuthenticators]);
  const handleCloseDrawer = useCallback(() => {
    if (passkeyMenuModalState !== void 0 && isMobileWeb) {
      setPasskeyMenuModalState(void 0);
    } else {
      onClose();
    }
  }, [onClose, passkeyMenuModalState]);
  const handleAddPasskey = useEvent(() => {
    setActionAfterVerify(PasskeyMenuModalState.ADD_PASSKEY);
    setPasskeyMenuModalState(PasskeyMenuModalState.VERIFY_PASSKEY);
  });
  const handleDeletePasskey = useEvent((authenticator) => {
    setSelectedAuthenticator(authenticator);
    setActionAfterVerify(PasskeyMenuModalState.DELETE_PASSKEY_SPEEDBUMP);
    setPasskeyMenuModalState(PasskeyMenuModalState.VERIFY_PASSKEY);
  });
  const handleVerification = useCallback(async () => {
    if (!actionAfterVerify) {
      return;
    }
    verifyPasskey();
  }, [actionAfterVerify, verifyPasskey]);
  return <>
      <Flex
    display={passkeyMenuModalState !== void 0 && !isMobileWeb ? "flex" : "none"}
    position="absolute"
    top={0}
    left={0}
    right={0}
    bottom={0}
    backgroundColor="$scrim"
    zIndex={1}
    opacity={0.6}
    borderRadius={isMobileWeb ? 0 : 12}
    alignItems="center"
    justifyContent="center"
    onPress={() => setPasskeyMenuModalState(void 0)}
  />

      <SlideOutMenu
    title={t2("common.passkeys")}
    onClose={handleCloseDrawer}
    rightIcon={<Anchor
      target="_blank"
      rel="noreferrer"
      href={uniswapUrls.helpArticleUrls.passkeysInfo}
      {...ClickableTamaguiStyle}
    >
            <LifeBuoy size={20} color={colors.neutral2.val} />
          </Anchor>}
  >
        <VerifyPasskeyMenu
    show={passkeyMenuModalState === PasskeyMenuModalState.VERIFY_PASSKEY}
    onVerify={handleVerification}
    onClose={() => setPasskeyMenuModalState(void 0)}
  />
        <AddPasskeyMenu
    show={passkeyMenuModalState === PasskeyMenuModalState.ADD_PASSKEY}
    setPasskeyMenuModalState={setPasskeyMenuModalState}
    refreshAuthenticators={refreshAuthenticators}
    credential={credential}
    numAuthenticators={authenticators.length}
  />
        <DeletePasskeySpeedbumpMenu
    show={passkeyMenuModalState === PasskeyMenuModalState.DELETE_PASSKEY_SPEEDBUMP}
    setPasskeyMenuModalState={setPasskeyMenuModalState}
  />
        {selectedAuthenticator && <DeletePasskeyMenu
    show={passkeyMenuModalState === PasskeyMenuModalState.DELETE_PASSKEY}
    setPasskeyMenuModalState={setPasskeyMenuModalState}
    refreshAuthenticators={refreshAuthenticators}
    authenticator={selectedAuthenticator}
    isLastAuthenticator={authenticators.length === 1}
    credential={credential}
  />}
        {passkeyMenuModalState === void 0 || !isMobileWeb ? <MenuColumn gap="12px">
            {!areAuthenticatorsLoading && authenticators.length ? <>
                {authenticators.map((authenticator) => <AuthenticatorRow
    key={authenticator.id}
    authenticator={authenticator}
    handleDeletePasskey={handleDeletePasskey}
  />)}
                <Flex row alignSelf="stretch">
                  <Button py="$padding16" variant="branded" emphasis="secondary" onPress={handleAddPasskey}>
                    <Text variant="buttonLabel2" color="$neutral2Temp">
                      {t2("common.passkeys.add")}
                    </Text>
                  </Button>
                </Flex>
              </> : Array.from({ length: 3 }).map((_, index) => <LoadingPasskeyRow key={index} />)}
          </MenuColumn> : null}
      </SlideOutMenu>
    </>;
}
